import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { ArrowRight, ArrowLeftRight, Trash2, Save, Loader2, Cable, Layers } from 'lucide-react';

export default function IntegrationEditorModal({
  open,
  onOpenChange,
  sourceNode,
  targetNode,
  existingIntegration,
  onSave,
  onDelete,
  onSaveProductIntegration,
  saving,
  isPropertyView,
}) {
  const [direction, setDirection] = useState('two-way');
  const [label, setLabel] = useState('');
  const [activeLevel, setActiveLevel] = useState('platform');
  // Product-level fields
  const [fromProductId, setFromProductId] = useState('');
  const [toProductId, setToProductId] = useState('');
  const [productStatus, setProductStatus] = useState('possible');
  const [productLabel, setProductLabel] = useState('');

  useEffect(() => {
    if (open) {
      if (existingIntegration) {
        setDirection(existingIntegration.direction || 'two-way');
        setLabel(existingIntegration.label || '');
      } else {
        setDirection('two-way');
        setLabel('');
      }
      setActiveLevel('platform');
      setFromProductId('');
      setToProductId('');
      setProductStatus('possible');
      setProductLabel('');
    }
  }, [open, existingIntegration]);

  const handleSave = () => {
    if (activeLevel === 'product' && fromProductId && toProductId && onSaveProductIntegration) {
      onSaveProductIntegration({
        from_product_id: fromProductId,
        to_product_id: toProductId,
        direction,
        status: productStatus,
        label: productLabel,
      });
    } else {
      onSave({
        from_platform_id: sourceNode?.id,
        to_platform_id: targetNode?.id,
        direction,
        label,
      });
    }
  };

  const handleDelete = () => {
    onDelete({
      from_platform_id: sourceNode?.id,
      to_platform_id: targetNode?.id,
    });
  };

  if (!sourceNode || !targetNode) return null;

  const sourceSolutions = sourceNode.solutions || [];
  const targetSolutions = targetNode.solutions || [];
  const hasProducts = isPropertyView && (sourceSolutions.length > 0 || targetSolutions.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="integration-editor-modal">
        <DialogHeader>
          <DialogTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {existingIntegration ? 'Edit Integration' : 'Create Integration'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Connect these two platforms{hasProducts ? ' — choose platform or product level' : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Source → Target display */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border">
            <div className="flex-1 text-center">
              <Badge variant="outline" className="text-[10px] mb-1" style={{ backgroundColor: `${sourceNode.category_color}12`, borderColor: `${sourceNode.category_color}40`, color: sourceNode.category_color }}>
                {sourceNode.category_name}
              </Badge>
              <p className="text-sm font-semibold" data-testid="integration-source-name">{sourceNode.short || sourceNode.name}</p>
              {sourceSolutions.length > 0 && (
                <p className="text-[10px] text-muted-foreground mt-0.5">{sourceSolutions.map(s => s.product_name || s.vendor_name).join(', ')}</p>
              )}
            </div>
            <div className="shrink-0 px-2">
              {direction === 'two-way' ? <ArrowLeftRight className="h-5 w-5 text-muted-foreground" /> : <ArrowRight className="h-5 w-5 text-muted-foreground" />}
            </div>
            <div className="flex-1 text-center">
              <Badge variant="outline" className="text-[10px] mb-1" style={{ backgroundColor: `${targetNode.category_color}12`, borderColor: `${targetNode.category_color}40`, color: targetNode.category_color }}>
                {targetNode.category_name}
              </Badge>
              <p className="text-sm font-semibold" data-testid="integration-target-name">{targetNode.short || targetNode.name}</p>
              {targetSolutions.length > 0 && (
                <p className="text-[10px] text-muted-foreground mt-0.5">{targetSolutions.map(s => s.product_name || s.vendor_name).join(', ')}</p>
              )}
            </div>
          </div>

          {/* Level toggle — platform vs product */}
          {hasProducts && (
            <div className="flex items-center gap-1 bg-muted/60 rounded-lg p-0.5">
              <button
                onClick={() => setActiveLevel('platform')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeLevel === 'platform' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
                data-testid="level-platform"
              >
                <Layers className="h-3 w-3" /> Platform Level
              </button>
              <button
                onClick={() => setActiveLevel('product')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeLevel === 'product' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
                data-testid="level-product"
              >
                <Cable className="h-3 w-3" /> Product Level
              </button>
            </div>
          )}

          <Separator />

          {activeLevel === 'platform' ? (
            <>
              {/* Direction */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Direction</Label>
                <Select value={direction} onValueChange={setDirection}>
                  <SelectTrigger data-testid="integration-direction-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one-way"><span className="flex items-center gap-2"><ArrowRight className="h-3.5 w-3.5" /> One-way ({sourceNode.short} → {targetNode.short})</span></SelectItem>
                    <SelectItem value="two-way"><span className="flex items-center gap-2"><ArrowLeftRight className="h-3.5 w-3.5" /> Two-way (Bidirectional)</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Label */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Label <span className="font-normal">(optional)</span></Label>
                <Input data-testid="integration-label-input" placeholder="e.g., Rate sync, Guest profile, Reservation data..." value={label} onChange={e => setLabel(e.target.value)} className="text-sm" />
              </div>
            </>
          ) : (
            <>
              {/* Product selection */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{sourceNode.short} Solution</Label>
                  <Select value={fromProductId} onValueChange={setFromProductId}>
                    <SelectTrigger className="text-xs" data-testid="product-from-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {sourceSolutions.map(s => (
                        <SelectItem key={s.product_id || s.vendor_name} value={s.product_id || `vendor:${s.vendor_name}`}>
                          {s.product_name || s.vendor_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{targetNode.short} Solution</Label>
                  <Select value={toProductId} onValueChange={setToProductId}>
                    <SelectTrigger className="text-xs" data-testid="product-to-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {targetSolutions.map(s => (
                        <SelectItem key={s.product_id || s.vendor_name} value={s.product_id || `vendor:${s.vendor_name}`}>
                          {s.product_name || s.vendor_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Direction + Status */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Direction</Label>
                  <Select value={direction} onValueChange={setDirection}>
                    <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="two-way">Two-way</SelectItem>
                      <SelectItem value="one-way">One-way</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={productStatus} onValueChange={setProductStatus}>
                    <SelectTrigger className="text-xs" data-testid="product-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Active (live)</span></SelectItem>
                      <SelectItem value="possible"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500" /> Possible (supported)</span></SelectItem>
                      <SelectItem value="missing"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-400" /> Missing (needed)</span></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Label <span className="font-normal">(optional)</span></Label>
                <Input value={productLabel} onChange={e => setProductLabel(e.target.value)} placeholder="e.g., Work Orders, Guest Data..." className="text-sm" />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex items-center gap-2">
          {existingIntegration && activeLevel === 'platform' && (
            <Button variant="destructive" size="sm" onClick={handleDelete} disabled={saving} data-testid="integration-delete-button" className="mr-auto gap-1.5">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || (activeLevel === 'product' && (!fromProductId || !toProductId))}
            data-testid="integration-save-button"
            className="gap-1.5"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {existingIntegration && activeLevel === 'platform' ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
